import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG } from '../config';
import { DeliveryUsage, QuotaDashboard, QuotaDimension } from '../models';

/**
 * Service for fetching and interpreting organization quota usage.
 *
 * Quota data reflects the plan limits and current resource consumption
 * for an organization (storage, posts, members, series, etc.).
 */
@Injectable({
  providedIn: 'root',
})
export class QuotaService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl: string;

  constructor() {
    const config = inject(USER_MANAGEMENT_API_CONFIG);
    this._apiUrl = config.apiBaseUrl;
  }

  /**
   * Fetches the complete quota dashboard for an organization.
   * Includes storage and count dimensions with usage percentages and warnings.
   *
   * @param orgId MongoDB _id of the organization
   */
  getQuotaDashboard(orgId: string): Observable<QuotaDashboard> {
    return this._http
      .get<{ success: boolean; data: QuotaDashboard }>(
        `${this._apiUrl}/api/organizations/${orgId}/quota-dashboard`,
      )
      .pipe(
        map(res => res.data),
        catchError(this._handleError('getQuotaDashboard')),
      );
  }

  /**
   * Synchronous helper: returns true when a quota dimension is at or above 80%.
   *
   * - `limit: null`  → unlimited, always returns false
   * - `limit: 0`     → not available on plan; returns false (not a quota concern)
   * - `pct >= 80`    → returns true
   *
   * @param dimension A single quota dimension from QuotaDashboard
   */
  isNearLimit(dimension: QuotaDimension): boolean {
    if (dimension.limit === null || dimension.limit === 0) return false;
    return (dimension.pct ?? 0) >= 80;
  }

  /** True when delivery `pct >= 90` (danger threshold). `limit: null` → always false. */
  isDeliveryNearLimit(delivery: DeliveryUsage): boolean {
    if (delivery.limit === null || delivery.limit === 0) return false;
    return (delivery.pct ?? 0) >= 90;
  }

  /** True when the delivery hard cap has been reached (`used >= limit`). */
  isDeliveryCapExceeded(delivery: DeliveryUsage): boolean {
    if (delivery.limit === null) return false;
    return delivery.used >= delivery.limit;
  }

  private _handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      console.error(`[QuotaService] ${operation} failed:`, error);
      return throwError(() => error);
    };
  }
}
